import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Lock, 
  Unlock, 
  Mail, 
  Users, 
  Package, 
  Building2,
  Target,
  ChevronDown,
  CheckCircle2,
  XCircle,
  Award,
  MapPin,
  Calendar,
  Zap,
  Globe,
  Shield,
  Sparkles,
  Hospital,
  ShieldCheck,
  Lightbulb,
  Rocket,
  AlertTriangle,
  Clock,
  Timer,
  MessageSquare,
  Play,
  ExternalLink,
  Phone,
  Wrench,
  QrCode,
  ClipboardList,
  Bell,
  FileCheck,
  Truck,
  Settings,
  BarChart3,
  Activity,
  Layers,
  Briefcase,
  TrendingDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ShareButtons } from "@/components/ShareButtons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// George Broughton Image
const GEORGE_IMAGE = "https://customer-assets.emergentagent.com/job_service-widgets/artifacts/n8vnfz06_Gemini_Generated_Image_ntzefxntzefxntze%20%281%29.png";

// Digital Isaac - AI Healthcare Assistant
const DIGITAL_ISAAC = "https://static.prod-images.emergentagent.com/jobs/0bc119bf-ae15-43e9-9f13-14b53e074c94/images/fe24d5e280d09d6a7d0c6d06f6b149605eaa9a0fa1be0b67e18ee25456bf0b6f.png";
const ASKISAAC_LOGO = "/logos/askisaac-b-monogram.svg";

// Beta benefits for hospitals
const betaBenefits = [
  {
    title: "Eliminate the $500K Downtime Leak",
    description: "Use our QR-code asset registry and digital maintenance dashboard to track every piece of equipment in real-time.",
    icon: QrCode,
    color: "from-blue-500 to-cyan-500"
  },
  {
    title: "Bypass the 3-Quote Bottleneck",
    description: "Our QuickBid feature allows you to access pre-vetted, credentialed contractors who can solve emergencies in hours, not weeks.",
    icon: Zap,
    color: "from-emerald-500 to-teal-500"
  },
  {
    title: "Hospital-Grade Security Suite",
    description: "Zero-Knowledge password architecture, MFA, 30-minute session timeouts, WORM audit logs, and PHI detection — IT Director approved.",
    icon: Shield,
    color: "from-violet-500 to-purple-500"
  },
  {
    title: "Isaac Bot — Telegram + Web Chat",
    description: "Submit requests, run demo simulations, and get real-time updates via Telegram @askisaac_bot or the Ask Isaac web chat on desktop.",
    icon: MessageSquare,
    color: "from-cyan-500 to-blue-500"
  },
  {
    title: "Extended Supply Network",
    description: "Complement your GPO with verified secondary providers offering immediate inventory and competitive pricing.",
    icon: Truck,
    color: "from-amber-500 to-orange-500"
  },
  {
    title: "STAT Medical Courier Network",
    description: "GPS-verified medical deliveries with certified couriers for lab specimens, blood products, and critical supplies — 100-yard geo-fence proof of delivery.",
    icon: MapPin,
    color: "from-red-500 to-rose-500"
  }
];

// Platform features
const platformFeatures = [
  {
    title: "Digital Maintenance Requests",
    description: "Submit via dashboard, Telegram, or Ask Isaac web chat — track everything in one place",
    icon: ClipboardList
  },
  {
    title: "QR-Code Asset Registry",
    description: "Scan any equipment to instantly access service history, manuals, and compliance data",
    icon: QrCode
  },
  {
    title: "Real-Time Notifications",
    description: "Get instant alerts on request status, contractor arrivals, and compliance deadlines",
    icon: Bell
  },
  {
    title: "Full Lifecycle Demo Simulations",
    description: "Use /demo to run 6-step Maintenance & Backorder simulations for training or presentations",
    icon: Play
  },
  {
    title: "Vendor Performance Tracking",
    description: "Rate contractors, track response times, and ensure quality service delivery",
    icon: BarChart3
  },
  {
    title: "Hospital-Grade Security",
    description: "Zero-Knowledge architecture, MFA, session timeouts, WORM audit logs — fully HIPAA-ready",
    icon: ShieldCheck
  }
];

// Case studies
const caseStudies = [
  {
    name: "Texas Medical Center",
    description: "Live Beta deployment at major healthcare facility",
    highlight: "Active Beta",
    result: "Real-time equipment tracking across departments",
    color: "from-blue-500 to-cyan-500"
  },
  {
    name: "Federal Healthcare Systems",
    description: "Nationwide reach through SDVOSB partnerships",
    highlight: "Federal Ready",
    result: "Compliant with federal procurement requirements",
    color: "from-emerald-500 to-teal-500"
  }
];

// FAQ for hospitals
const hospitalFaqData = [
  {
    question: "What does the Beta program include?",
    answer: "Full access to the AskIsaac platform including digital maintenance requests, QR-code asset registry, QuickBid contractor matching, and automated compliance reporting. Beta partners also get direct input on feature development."
  },
  {
    question: "How long does implementation take?",
    answer: "Most facilities are fully operational within 2-4 weeks. We handle QR code generation, staff training, and system configuration as part of the onboarding process."
  },
  {
    question: "Is there a cost to join the Beta?",
    answer: "Beta partners receive significantly discounted pricing in exchange for feedback and case study participation. Contact us for specific terms."
  },
  {
    question: "How does QuickBid work?",
    answer: "When you need emergency service, post your requirement and receive 3+ compliant bids from pre-vetted local contractors within hours. No more manual quote gathering."
  },
  {
    question: "What compliance standards do you support?",
    answer: "We provide digital validation tools for AAMI ST108, Joint Commission requirements, and can customize reporting for your specific compliance needs."
  },
  {
    question: "Can we integrate with our existing systems?",
    answer: "Yes, AskIsaac offers API integrations with common hospital ERP, CMMS, and financial systems. We'll work with your IT team during onboarding."
  }
];

export default function HospitalPresentationPage() {
  const navigate = useNavigate();
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState("overview");
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [contactForm, setContactForm] = useState({ name: "", email: "", facility: "", message: "" });
  const [formSubmitting, setFormSubmitting] = useState(false);

  useEffect(() => {
    // Check if already unlocked in this session
    const unlockedEmail = sessionStorage.getItem("hospital_presentation_unlocked");
    if (unlockedEmail) {
      setIsUnlocked(true);
      setEmail(unlockedEmail);
    }
  }, []);

  const handleUnlock = (e) => {
    e.preventDefault();
    setLoading(true);
    
    setTimeout(() => {
      // For hospital presentation, we capture email for follow-up but allow all access
      const normalizedEmail = email.toLowerCase().trim();
      if (normalizedEmail && normalizedEmail.includes("@")) {
        setIsUnlocked(true);
        sessionStorage.setItem("hospital_presentation_unlocked", normalizedEmail);
        toast.success("Welcome! Explore the AskIsaac Beta Program.");
        // In production, you would send this email to your CRM/database
      } else {
        toast.error("Please enter a valid email address.");
      }
      setLoading(false);
    }, 1000);
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setTimeout(() => {
      toast.success("Thank you! Our team will contact you within 24 hours to discuss the Beta program.");
      setContactForm({ name: "", email: "", facility: "", message: "" });
      setFormSubmitting(false);
    }, 1000);
  };

  // Presentation switcher component
  const PresentationSwitcher = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-2 bg-slate-800/50 border-slate-600 hover:bg-slate-700 text-white">
          <Layers className="w-4 h-4" />
          <span className="hidden sm:inline">Presentations</span>
          <ChevronDown className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
        <DropdownMenuItem 
          onClick={() => navigate("/hospital-presentation")}
          className="text-white hover:bg-slate-700 cursor-pointer"
        >
          <Hospital className="w-4 h-4 mr-2 text-cyan-400" />
          Hospital Beta Program
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => navigate("/investor")}
          className="text-white hover:bg-slate-700 cursor-pointer"
        >
          <Briefcase className="w-4 h-4 mr-2 text-violet-400" />
          Investor Presentation
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (!isUnlocked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-6">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="relative z-10 w-full max-w-md">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shadow-2xl shadow-cyan-500/30 animate-pulse">
              <Hospital className="w-12 h-12 text-white" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold text-white text-center mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Hospital Beta Program
          </h1>
          <p className="text-slate-400 text-center mb-2">
            AskIsaac.io — Solving the $500K Downtime Crisis
          </p>
          <p className="text-cyan-400 text-center mb-8 text-sm">
            Enter your email to access the presentation
          </p>

          {/* Email Form */}
          <form onSubmit={handleUnlock} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="email"
                placeholder="your.email@hospital.org"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 rounded-xl focus:border-cyan-500 focus:ring-cyan-500/20"
                required
                data-testid="hospital-email-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-cyan-600 to-blue-500 hover:from-cyan-700 hover:to-blue-600 text-white font-semibold rounded-xl shadow-lg shadow-cyan-500/25 transition-all"
              data-testid="hospital-unlock-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Loading...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Unlock className="w-5 h-5" />
                  View Presentation
                </span>
              )}
            </Button>
          </form>

          <p className="text-slate-500 text-sm text-center mt-6">
            Questions? Contact <a href="mailto:info@sterilemate.com" className="text-cyan-400 hover:text-cyan-300">info@sterilemate.com</a>
          </p>

          {/* Switcher link */}
          <div className="mt-8 text-center">
            <button 
              onClick={() => navigate("/investor")}
              className="text-slate-500 hover:text-slate-300 text-sm flex items-center gap-2 mx-auto"
            >
              Looking for the Investor Presentation? <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Background effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-slate-700/50 bg-slate-900/80 backdrop-blur-lg sticky top-0">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <img src={ASKISAAC_LOGO} alt="AskIsaac" className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg" />
            <div>
              <h1 className="font-bold text-sm sm:text-lg">AskIsaac.io</h1>
              <p className="text-[10px] sm:text-xs text-cyan-400">Hospital Beta Program</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <PresentationSwitcher />
            <ShareButtons 
              title="AskIsaac - Hospital Beta Program"
              description="Solving the $500K Hospital Downtime Crisis. Join our Beta."
              url={window.location.href}
              variant="dark"
              size="sm"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                sessionStorage.removeItem("hospital_presentation_unlocked");
                setIsUnlocked(false);
                setEmail("");
              }}
              className="text-slate-400 hover:text-white hover:bg-slate-800 text-xs sm:text-sm px-2 sm:px-3"
            >
              Exit
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="relative z-10 border-b border-slate-700/50 bg-slate-800/50 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 py-2 sm:py-3 flex gap-1 sm:gap-2 overflow-x-auto scrollbar-hide">
          {[
            { id: "overview", label: "Overview" },
            { id: "problem", label: "The Problem" },
            { id: "solution", label: "Solution" },
            { id: "features", label: "Features" },
            { id: "benefits", label: "Beta Benefits" },
            { id: "casestudies", label: "Case Studies" },
            { id: "faq", label: "FAQ" },
            { id: "contact", label: "Join Beta" }
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveSection(item.id)}
              className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium text-xs sm:text-sm whitespace-nowrap transition-all flex-shrink-0 ${
                activeSection === item.id
                  ? "bg-gradient-to-r from-cyan-600 to-blue-500 text-white shadow-lg"
                  : "text-slate-400 hover:text-white hover:bg-slate-700/50"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-3 sm:px-6 py-6 sm:py-10">
        
        {/* Overview Section */}
        {activeSection === "overview" && (
          <div className="space-y-10 animate-fade-in">
            {/* Hero Banner */}
            <div className="relative bg-gradient-to-br from-[#0a1628] via-[#0d1526] to-[#0a0f1a] rounded-3xl overflow-hidden border border-cyan-500/20">
              <div className="absolute inset-0 overflow-hidden">
                {[...Array(30)].map((_, i) => (
                  <div 
                    key={i}
                    className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                      opacity: Math.random() * 0.5 + 0.2,
                      animationDelay: `${Math.random() * 3}s`
                    }}
                  />
                ))}
              </div>
              
              <div className="relative z-10 grid lg:grid-cols-2 gap-8 p-8 lg:p-12">
                <div className="flex flex-col justify-center">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm mb-6 w-fit">
                    <Sparkles className="w-4 h-4" />
                    Exclusive Beta Program
                  </div>
                  
                  <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Solve the $500K<br />Downtime Crisis
                  </h1>
                  
                  <p className="text-xl text-slate-300 leading-relaxed mb-8">
                    Join our Beta program and discover how <span className="text-cyan-400 font-semibold">AskIsaac.io</span> connects your facility to pre-vetted local contractors when Big Box providers fail.
                  </p>
                  
                  <div className="flex flex-wrap gap-3">
                    <a 
                      href="https://sterilemate.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-500 hover:from-cyan-700 hover:to-blue-600 text-white font-semibold rounded-xl shadow-lg shadow-cyan-500/25 transition-all"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Visit AskIsaac.io
                    </a>
                    <button 
                      onClick={() => setActiveSection("contact")}
                      className="inline-flex items-center gap-2 px-6 py-3 border border-slate-600 hover:border-cyan-500/50 text-slate-300 hover:text-white font-medium rounded-xl transition-all"
                    >
                      <Rocket className="w-4 h-4" />
                      Join the Beta
                    </button>
                  </div>
                </div>
                
                <div className="relative flex items-center justify-center">
                  <div className="absolute inset-0 bg-gradient-radial from-cyan-500/20 via-blue-500/10 to-transparent blur-3xl" />
                  <img 
                    src={DIGITAL_ISAAC}
                    alt="Isaac - Your Digital Healthcare Assistant"
                    className="relative z-10 w-full max-w-md h-auto object-contain"
                    style={{ 
                      filter: 'drop-shadow(0 0 60px rgba(96, 165, 250, 0.4))',
                      animation: 'floatIsaac 6s ease-in-out infinite'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Key Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Annual Downtime Cost", value: "$500K+", icon: AlertTriangle, color: "text-red-400" },
                { label: "Quote Time Reduction", value: "90%", icon: Clock, color: "text-emerald-400" },
                { label: "Pre-Vetted Contractors", value: "100+", icon: Users, color: "text-cyan-400" },
                { label: "Compliance Ready", value: "100%", icon: ShieldCheck, color: "text-violet-400" }
              ].map((stat, idx) => (
                <div key={idx} className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 text-center">
                  <stat.icon className={`w-8 h-8 mx-auto mb-3 ${stat.color}`} />
                  <p className="text-2xl font-bold mb-1">{stat.value}</p>
                  <p className="text-sm text-slate-400">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* What is AskIsaac */}
            <div className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border border-slate-700/50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <Lightbulb className="w-6 h-6 text-cyan-400" />
                What is AskIsaac?
              </h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                A hospital is a <span className="text-amber-400 font-semibold">critical environment</span>. When something breaks, it&apos;s not an inconvenience—it&apos;s a clinical risk. Yet your front-line staff are stuck with phone tag, paper forms, and vendor voicemail while patients wait.
              </p>
              <p className="text-slate-300 leading-relaxed mb-4">
                <span className="text-cyan-400 font-semibold">AskIsaac.io</span> gives your front-line workers the power to solve problems <span className="text-emerald-400 font-semibold">in the moment</span>—triggering a dedicated engine that knows what they need and brings everyone together under one hood.
              </p>
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-4 mt-4">
                <p className="text-cyan-400 font-semibold italic text-center">
                  &quot;Your front-line staff deserve tools that work as hard as they do.&quot;
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Problem Section */}
        {activeSection === "problem" && (
          <div className="space-y-10 animate-fade-in">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                The Reality on Your Floor Right Now
              </h1>
              <p className="text-xl text-slate-400">Why your front-line staff are frustrated—and patients are waiting</p>
            </div>

            {/* Visceral Pain Statement */}
            <div className="relative bg-gradient-to-br from-red-950/40 via-slate-900 to-slate-900 rounded-3xl overflow-hidden border border-red-500/30 p-8">
              <div className="absolute top-0 right-0 w-64 h-64 bg-red-500/10 rounded-full blur-3xl" />
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-500/40 rounded-full text-red-400 text-sm mb-6">
                  <AlertTriangle className="w-4 h-4" />
                  Every Day in Healthcare
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold mb-4 leading-tight">
                  Your SPD manager needs a critical part. <span className="text-amber-400 font-bold">Now.</span><br />
                  Your nurse is on hold with three vendors.<br />
                  <span className="text-slate-500">Your facilities tech is playing phone tag at 2 AM.</span>
                </h2>
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-5">
                  <p className="text-lg text-white mb-2">
                    They&apos;re <strong className="text-emerald-400">excellent</strong> at their jobs. But right now, they&apos;re stuck with <span className="text-red-400 font-semibold">paper forms, vendor voicemail, and approval chains</span>—while patients wait.
                  </p>
                  <p className="text-xl font-bold text-amber-400">That&apos;s $50,000 in lost revenue PER INCIDENT.</p>
                </div>
              </div>
            </div>

            {/* The Real Problem */}
            <div className="bg-gradient-to-br from-amber-900/20 to-orange-900/20 border border-amber-500/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-amber-400">
                <AlertTriangle className="w-6 h-6" />
                The Real Problem: Front-Line Disconnection
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed mb-6">
                Your front-line workers make critical decisions every day. But they don&apos;t have tools that match the urgency of their environment. They&apos;re forced to use systems designed for back-office convenience, not floor-level speed.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                  <Package className="w-8 h-8 text-red-400 mb-3" />
                  <h3 className="font-semibold mb-2">Backorder Bottlenecks</h3>
                  <p className="text-slate-400 text-sm">Critical supplies stuck in limbo while patients wait. Large distributors simply quote a date — they don&apos;t solve the problem.</p>
                </div>
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
                  <Wrench className="w-8 h-8 text-red-400 mb-3" />
                  <h3 className="font-semibold mb-2">Service Provider Gaps</h3>
                  <p className="text-slate-400 text-sm">When your contracted provider can&apos;t respond, equipment sits idle. Hospitals need options, not excuses.</p>
                </div>
              </div>
            </div>

            {/* Competitor Comparison Chart */}
            <div className="bg-gradient-to-br from-slate-800/80 to-slate-900 border border-slate-700/50 rounded-2xl p-6 sm:p-8 overflow-hidden">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold">Why Traditional Solutions Fail</h2>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-4 px-3 text-slate-400 font-medium text-sm">Capability</th>
                      <th className="text-center py-4 px-3 text-slate-500 font-medium text-sm">Big Box<br/><span className="text-xs font-normal">Distributors</span></th>
                      <th className="text-center py-4 px-3 text-slate-500 font-medium text-sm">Your ERP<br/><span className="text-xs font-normal">System</span></th>
                      <th className="text-center py-4 px-3 text-slate-500 font-medium text-sm">Manual<br/><span className="text-xs font-normal">Process</span></th>
                      <th className="text-center py-4 px-3 font-medium text-sm bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-t-lg border-x border-t border-cyan-500/30">
                        <span className="text-cyan-400">AskIsaac</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-slate-800">
                      <td className="py-4 px-3 text-white font-medium">Mobile Request Submission</td>
                      <td className="py-4 px-3 text-center"><XCircle className="w-5 h-5 text-red-400 mx-auto" /></td>
                      <td className="py-4 px-3 text-center"><XCircle className="w-5 h-5 text-red-400 mx-auto" /></td>
                      <td className="py-4 px-3 text-center"><XCircle className="w-5 h-5 text-red-400 mx-auto" /></td>
                      <td className="py-4 px-3 text-center bg-cyan-500/5 border-x border-cyan-500/30"><CheckCircle2 className="w-5 h-5 text-emerald-400 mx-auto" /></td>
                    </tr>
                    <tr className="border-b border-slate-800">
                      <td className="py-4 px-3 text-white font-medium">Instant Competitive Quotes</td>
                      <td className="py-4 px-3 text-center"><XCircle className="w-5 h-5 text-red-400 mx-auto" /></td>
                      <td className="py-4 px-3 text-center"><XCircle className="w-5 h-5 text-red-400 mx-auto" /></td>
                      <td className="py-4 px-3 text-center"><XCircle className="w-5 h-5 text-red-400 mx-auto" /></td>
                      <td className="py-4 px-3 text-center bg-cyan-500/5 border-x border-cyan-500/30"><CheckCircle2 className="w-5 h-5 text-emerald-400 mx-auto" /></td>
                    </tr>
                    <tr className="border-b border-slate-800">
                      <td className="py-4 px-3 text-white font-medium">Pre-Vetted Local Contractors</td>
                      <td className="py-4 px-3 text-center"><XCircle className="w-5 h-5 text-red-400 mx-auto" /></td>
                      <td className="py-4 px-3 text-center"><XCircle className="w-5 h-5 text-red-400 mx-auto" /></td>
                      <td className="py-4 px-3 text-center text-amber-400 text-xs">Manual Search</td>
                      <td className="py-4 px-3 text-center bg-cyan-500/5 border-x border-cyan-500/30"><CheckCircle2 className="w-5 h-5 text-emerald-400 mx-auto" /></td>
                    </tr>
                    <tr className="border-b border-slate-800">
                      <td className="py-4 px-3 text-white font-medium">GPS-Verified Delivery</td>
                      <td className="py-4 px-3 text-center text-amber-400 text-xs">Limited</td>
                      <td className="py-4 px-3 text-center"><XCircle className="w-5 h-5 text-red-400 mx-auto" /></td>
                      <td className="py-4 px-3 text-center"><XCircle className="w-5 h-5 text-red-400 mx-auto" /></td>
                      <td className="py-4 px-3 text-center bg-cyan-500/5 border-x border-cyan-500/30"><CheckCircle2 className="w-5 h-5 text-emerald-400 mx-auto" /></td>
                    </tr>
                    <tr className="border-b border-slate-800">
                      <td className="py-4 px-3 text-white font-medium">Auto Compliance Audit Trail</td>
                      <td className="py-4 px-3 text-center"><XCircle className="w-5 h-5 text-red-400 mx-auto" /></td>
                      <td className="py-4 px-3 text-center text-amber-400 text-xs">Complex</td>
                      <td className="py-4 px-3 text-center"><XCircle className="w-5 h-5 text-red-400 mx-auto" /></td>
                      <td className="py-4 px-3 text-center bg-cyan-500/5 border-x border-cyan-500/30"><CheckCircle2 className="w-5 h-5 text-emerald-400 mx-auto" /></td>
                    </tr>
                    <tr>
                      <td className="py-4 px-3 text-white font-medium">Time to Quote</td>
                      <td className="py-4 px-3 text-center text-red-400 font-semibold">Days</td>
                      <td className="py-4 px-3 text-center text-red-400 font-semibold">N/A</td>
                      <td className="py-4 px-3 text-center text-red-400 font-semibold">Weeks</td>
                      <td className="py-4 px-3 text-center bg-cyan-500/5 border-x border-b border-cyan-500/30 rounded-b-lg text-emerald-400 font-bold">Hours</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Day in the Life - Before/After */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* BEFORE */}
              <div className="bg-gradient-to-br from-red-950/30 to-slate-900 border border-red-500/30 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                  </div>
                  <h3 className="text-xl font-bold text-red-400">Without AskIsaac</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg border border-red-500/20">
                    <Clock className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-white">8:00 AM</p>
                      <p className="text-sm text-slate-400">Equipment fails. Manager calls vendor, waits 45 minutes on hold.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg border border-red-500/20">
                    <Clock className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-white">10:30 AM</p>
                      <p className="text-sm text-slate-400">Still gathering quotes. Afternoon procedures at risk.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg border border-red-500/20">
                    <Clock className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-white">2:00 PM</p>
                      <p className="text-sm text-slate-400">Procedures delayed. Revenue lost. Patient harm risk.</p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-center text-red-300 font-semibold">Result: $50K+ loss per incident</p>
                </div>
              </div>

              {/* AFTER */}
              <div className="bg-gradient-to-br from-emerald-950/30 to-slate-900 border border-emerald-500/30 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-bold text-emerald-400">With AskIsaac</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg border border-emerald-500/20">
                    <Clock className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-white">8:00 AM</p>
                      <p className="text-sm text-slate-400">Equipment fails. Manager texts Isaac bot from the floor. Done in 30 seconds.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg border border-emerald-500/20">
                    <Clock className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-white">8:45 AM</p>
                      <p className="text-sm text-slate-400">3 pre-vetted contractors bid. Lowest compliant bid wins automatically.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg border border-emerald-500/20">
                    <Clock className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-white">11:00 AM</p>
                      <p className="text-sm text-slate-400">Technician arrives. GPS-verified check-in. Fixed before lunch.</p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  <p className="text-center text-emerald-300 font-semibold">Result: No delays. Zero revenue loss.</p>
                </div>
              </div>
            </div>

            {/* Network Expansion Opportunity */}
            <div className="bg-gradient-to-br from-amber-900/20 to-orange-900/20 border border-amber-500/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-amber-400">
                <Users className="w-6 h-6" />
                Network Expansion Opportunity
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed mb-6">
                Healthcare organizations can <span className="text-amber-400 font-semibold">expand their supplier and contractor networks</span> with AskIsaac. Quality vendors are ready to serve — we help you find and validate their credentials quickly.
              </p>
              <div className="p-5 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                <p className="text-center text-amber-300 font-medium text-lg">
                  <Clock className="w-5 h-5 inline mr-2" />
                  Skip the manual 3-quote process. Get competitive bids in minutes.
                </p>
              </div>
            </div>

            {/* Over-Specification */}
            <div className="bg-gradient-to-br from-violet-900/20 to-purple-900/20 border border-violet-500/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-violet-400">
                <Target className="w-6 h-6" />
                The Over-Specification Drain
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed">
                Legacy providers frequently over-specify hardware — sometimes by over <span className="text-violet-400 font-semibold">$1 million for a single department&apos;s water system</span> — simply because they lack the data-driven validation to prove a leaner, more efficient solution works.
              </p>
            </div>
          </div>
        )}

        {/* Solution Section */}
        {activeSection === "solution" && (
          <div className="space-y-10 animate-fade-in">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Front-Line Empowerment
              </h1>
              <p className="text-xl text-slate-400">30 seconds from problem to response—not 30 minutes, not 3 days</p>
            </div>

            {/* The 30-Second Flow */}
            <div className="bg-gradient-to-br from-emerald-900/30 to-cyan-900/30 border border-emerald-500/30 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-emerald-400">
                <Zap className="w-6 h-6" />
                The 30-Second Response
              </h2>
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div className="bg-slate-800/50 rounded-xl p-5 border border-emerald-500/20">
                  <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center mb-4">
                    <span className="text-white font-bold text-lg">1</span>
                  </div>
                  <h3 className="font-bold text-white mb-2">Text Isaac from the Floor</h3>
                  <p className="text-sm text-slate-400">No desktop. No login. No forms. Just a message: &quot;Need P-trap for Room 203, leaking now.&quot;</p>
                </div>
                <div className="bg-slate-800/50 rounded-xl p-5 border border-emerald-500/20">
                  <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center mb-4">
                    <span className="text-white font-bold text-lg">2</span>
                  </div>
                  <h3 className="font-bold text-white mb-2">Engine Takes Over</h3>
                  <p className="text-sm text-slate-400">Auto-routes to vetted contractors. Triggers 3-quote compliance if needed. Notifies everyone who needs to know.</p>
                </div>
                <div className="bg-slate-800/50 rounded-xl p-5 border border-emerald-500/20">
                  <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center mb-4">
                    <span className="text-white font-bold text-lg">3</span>
                  </div>
                  <h3 className="font-bold text-white mb-2">Problem Solved</h3>
                  <p className="text-sm text-slate-400">GPS-verified contractor arrival. Photos of completed work. Audit trail complete—automatically.</p>
                </div>
              </div>
              <div className="p-5 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                <p className="text-center text-emerald-300 font-bold text-lg">
                  Total time: 30 seconds—not 30 minutes, not 3 days.
                </p>
              </div>
            </div>

            {/* We Deploy, Not Just Track */}
            <div className="bg-gradient-to-br from-cyan-900/20 to-blue-900/20 border border-cyan-500/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-cyan-400">
                <Target className="w-6 h-6" />
                We Deploy, Not Just Track
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed mb-6">
                AskIsaac isn&apos;t another dashboard to monitor. It&apos;s a <span className="text-cyan-400 font-semibold">dedicated engine</span> that knows what your team needs and brings everyone together under one hood.
              </p>
            </div>

            {/* Key Features */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-xl mb-3">Pre-Vetted Local Contractors</h3>
                <p className="text-slate-400">
                  Plumbing, electrical, HVAC—<span className="text-cyan-400 font-semibold">fully credentialed, ready to respond</span>. No more phone tag with unqualified vendors.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mb-4">
                  <Package className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-xl mb-3">Instant Backorder Sourcing</h3>
                <p className="text-slate-400">
                  When Big Box distributors fail, we find it fast from <span className="text-emerald-400 font-semibold">verified suppliers</span>. Zero canceled surgeries.
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-xl mb-3">GPS-Verified Response</h3>
                <p className="text-slate-400">
                  Know exactly when help arrives and leaves. <span className="text-violet-400 font-semibold">No more &quot;where&apos;s the tech?&quot;</span>
                </p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mb-4">
                  <ShieldCheck className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-xl mb-3">Auto-Compliance</h3>
                <p className="text-slate-400">
                  3-quote rules, AAMI ST108, Joint Commission documentation—<span className="text-amber-400 font-semibold">handled automatically</span>.
                </p>
              </div>
            </div>

            {/* Everyone Under One Hood */}
            <div className="bg-gradient-to-br from-amber-900/20 to-orange-900/20 border border-amber-500/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-amber-400">
                <Layers className="w-6 h-6" />
                Everyone Under One Hood
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed">
                When your nurse submits a request, the plumber, the supplier, the facilities manager, and administration <span className="text-amber-400 font-semibold">all see the same ticket, updated in real-time</span>. No more phone tag. No more &quot;I thought you were handling it.&quot; One source of truth for everyone.
              </p>
            </div>

            {/* Multi-Stakeholder Sync & Supplier Chain of Command */}
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <div className="bg-gradient-to-br from-cyan-900/30 to-blue-900/30 border border-cyan-500/30 rounded-2xl p-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-xl mb-3">Multi-Stakeholder Sync</h3>
                <p className="text-slate-400 mb-4">
                  Tag plumbers, nurses, and suppliers on any maintenance ticket. <span className="text-cyan-400 font-semibold">Everyone stays informed</span> with real-time email and Telegram notifications.
                </p>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Invite unregistered collaborators via email</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Viral growth loop for network expansion</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Complete audit trail for compliance</li>
                </ul>
              </div>
              
              <div className="bg-gradient-to-br from-amber-900/30 to-orange-900/30 border border-amber-500/30 rounded-2xl p-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-xl mb-3">Supplier Chain of Command</h3>
                <p className="text-slate-400 mb-4">
                  Suppliers manage their own teams with a <span className="text-amber-400 font-semibold">4-tier role hierarchy</span>. Less admin work for your facility.
                </p>
                <ul className="space-y-2 text-sm text-slate-300">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Account Principal → Full authority</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Field Supervisor → Job assignment & approval</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Technician → Execute & document work</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Authorized Buyer → Quote/backorder approval</li>
                </ul>
              </div>
            </div>

            {/* HIPAA Compliance / Secure Audit Vault */}
            <div className="bg-gradient-to-br from-emerald-900/30 to-teal-900/30 border border-emerald-500/30 rounded-2xl p-8 mt-6">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-3 text-emerald-400">
                <Lock className="w-6 h-6" />
                HIPAA-Ready Secure Audit Vault
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed mb-6">
                Every interaction—Telegram messages, status updates, photos, contractor check-ins—is <span className="text-emerald-400 font-semibold">instantly archived</span> to our Secure Vault with <span className="text-emerald-400 font-semibold">7-year retention</span>.
              </p>
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="bg-slate-800/50 rounded-xl p-4 border border-emerald-500/20">
                  <p className="text-emerald-400 font-bold mb-2">PII/PHI Scrubbing</p>
                  <p className="text-sm text-slate-400">Automatic redaction of SSN, phone, email, DOB, MRN patterns</p>
                </div>
                <div className="bg-slate-800/50 rounded-xl p-4 border border-emerald-500/20">
                  <p className="text-emerald-400 font-bold mb-2">Unalterable Records</p>
                  <p className="text-sm text-slate-400">Even if a user deletes a message, the hospital retains the record</p>
                </div>
                <div className="bg-slate-800/50 rounded-xl p-4 border border-emerald-500/20">
                  <p className="text-emerald-400 font-bold mb-2">Audit Export</p>
                  <p className="text-sm text-slate-400">One-click export for Joint Commission and regulatory audits</p>
                </div>
              </div>
              <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                <p className="text-emerald-300 font-medium italic text-center">
                  &quot;We don&apos;t use Telegram as a standalone app—we use it as a remote interface. Every byte of data is instantly archived to HIPAA-compliant infrastructure.&quot;
                </p>
              </div>
            </div>

            {/* Services Under One Roof */}
            <div className="bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border border-cyan-500/30 rounded-2xl p-8 mt-8">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold mb-2 flex items-center justify-center gap-3">
                  <Layers className="w-6 h-6 text-cyan-400" />
                  Everything Under One Roof
                </h2>
                <p className="text-slate-400">From first request to last — it's done. Call us again when you need us!</p>
              </div>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="bg-slate-800/60 rounded-xl p-5 text-center border border-slate-700/50 hover:border-blue-500/50 transition-all">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mx-auto mb-3">
                    <Settings className="w-7 h-7 text-white" />
                  </div>
                  <h4 className="font-bold text-white mb-2">Healthcare Water Purification</h4>
                  <ul className="text-xs text-slate-400 space-y-1">
                    <li>Installation & project mgmt</li>
                    <li>Maintenance & repairs</li>
                    <li>RO systems, DI, softeners</li>
                  </ul>
                </div>
                <div className="bg-slate-800/60 rounded-xl p-5 text-center border border-slate-700/50 hover:border-emerald-500/50 transition-all">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mx-auto mb-3">
                    <Wrench className="w-7 h-7 text-white" />
                  </div>
                  <h4 className="font-bold text-white mb-2">Licensed Contractors</h4>
                  <ul className="text-xs text-slate-400 space-y-1">
                    <li>Plumbing — healthcare exp.</li>
                    <li>Electrical — licensed</li>
                    <li>HVAC — critical env. certified</li>
                  </ul>
                </div>
                <div className="bg-slate-800/60 rounded-xl p-5 text-center border border-slate-700/50 hover:border-amber-500/50 transition-all">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mx-auto mb-3">
                    <Shield className="w-7 h-7 text-white" />
                  </div>
                  <h4 className="font-bold text-white mb-2">General Contractor</h4>
                  <ul className="text-xs text-slate-400 space-y-1">
                    <li>Containment & infection ctrl</li>
                    <li>Healthcare renovations</li>
                    <li>Compliance-first construction</li>
                  </ul>
                </div>
                <div className="bg-slate-800/60 rounded-xl p-5 text-center border border-slate-700/50 hover:border-violet-500/50 transition-all">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center mx-auto mb-3">
                    <ClipboardList className="w-7 h-7 text-white" />
                  </div>
                  <h4 className="font-bold text-white mb-2">Water Sampling</h4>
                  <ul className="text-xs text-slate-400 space-y-1">
                    <li>AAMI ST108 compliant</li>
                    <li>Lab testing coordination</li>
                    <li>Compliance documentation</li>
                  </ul>
                </div>
              </div>
              <div className="mt-6 p-4 bg-cyan-500/10 border border-cyan-500/20 rounded-xl">
                <p className="text-center text-cyan-300 font-medium">
                  <Zap className="w-4 h-4 inline mr-2" />
                  All contractors pre-vetted with healthcare experience — licensed, insured, and &quot;healthcare-ready&quot;
                </p>
              </div>
            </div>

            {/* Partnership Options - Three Tiers */}
            <div className="bg-gradient-to-br from-slate-800/80 to-slate-900 border border-slate-700/50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Briefcase className="w-6 h-6 text-amber-400" />
                Partnership Options — Three Engagement Tiers
              </h2>
              
              {/* Tier 1 */}
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5 mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="w-6 h-6 text-emerald-400" />
                  <h3 className="font-bold text-lg text-emerald-400">Tier 1: Managed Compliance</h3>
                </div>
                <p className="text-slate-300 text-sm mb-2"><strong>Core Value:</strong> We provide a &quot;Regulatory Shield&quot; for your facility.</p>
                <p className="text-slate-400 text-sm"><strong>Focus:</strong> Using trusted access to execute complex projects (e.g., Critical Water Loops) that &quot;outside&quot; contractors can&apos;t touch without oversight.</p>
              </div>

              {/* Tier 2 */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-5 mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <Globe className="w-6 h-6 text-blue-400" />
                  <h3 className="font-bold text-lg text-blue-400">Tier 2: Enterprise Marketplace</h3>
                </div>
                <p className="text-slate-300 text-sm mb-2"><strong>Core Value:</strong> Standardizing the &quot;Chaotic&quot; procurement process for your system.</p>
                <p className="text-slate-400 text-sm"><strong>Focus:</strong> Use your preferred vendors through our verified tracking and compliance reporting logic.</p>
              </div>

              {/* Tier 3 */}
              <div className="bg-violet-500/10 border border-violet-500/30 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Layers className="w-6 h-6 text-violet-400" />
                  <h3 className="font-bold text-lg text-violet-400">Tier 3: White-Label Infrastructure</h3>
                </div>
                <p className="text-slate-300 text-sm mb-2"><strong>Core Value:</strong> Handing the &quot;Keys to the Kingdom&quot; to your Internal Teams.</p>
                <p className="text-slate-400 text-sm"><strong>Focus:</strong> AskIsaac becomes your permanent digital record for all facility and infrastructure compliance.</p>
              </div>
            </div>

            {/* Strategic Moats */}
            <div className="bg-gradient-to-br from-amber-900/20 to-orange-900/20 border border-amber-500/30 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Lock className="w-6 h-6 text-amber-400" />
                Why AskIsaac — Our Strategic Advantages
              </h2>
              
              {/* Moat 1 */}
              <div className="mb-6">
                <h3 className="text-lg font-bold text-blue-400 mb-3">Institutional Access</h3>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span><strong>6 Years of Texas Medical Center (TMC) Trust</strong> — Direct relationships with facility leadership</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span><strong>SDVOSB Federal Gateway</strong> — Fast-track access to nationwide VA facilities</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span><strong>Tier 1 Public Health System Partnership</strong> — Proven integration with major health systems</span>
                  </li>
                </ul>
              </div>
              
              {/* Moat 2 */}
              <div>
                <h3 className="text-lg font-bold text-emerald-400 mb-3">Technical Capabilities</h3>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span><strong>Water Purification Solutions</strong> — AAMI ST108 water purification for Sterile Processing &amp; Dialysis</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span><strong>Enterprise Integration</strong> — Advanced Techinquest software integration for compliance automation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span><strong>Managed Compliance IP</strong> — Proprietary logic for regulatory compliance projects</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Features Section */}
        {activeSection === "features" && (
          <div className="space-y-10 animate-fade-in">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Platform Features
              </h1>
              <p className="text-xl text-slate-400">Everything you need to manage healthcare facilities</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {platformFeatures.map((feature, idx) => (
                <div key={idx} className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 hover:border-cyan-500/30 transition-all">
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-cyan-400" />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{feature.title}</h3>
                  <p className="text-slate-400 text-sm">{feature.description}</p>
                </div>
              ))}
            </div>

            {/* Demo CTA */}
            <div className="text-center bg-gradient-to-r from-cyan-600/20 to-blue-600/20 border border-cyan-500/30 rounded-2xl p-10">
              <h2 className="text-2xl font-bold mb-4">See It In Action</h2>
              <p className="text-slate-400 mb-6">Watch a 2-minute demo of the AskIsaac platform</p>
              <a 
                href="https://sterilemate.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-cyan-600 to-blue-500 hover:from-cyan-700 hover:to-blue-600 text-white font-bold rounded-xl shadow-lg shadow-cyan-500/30 transition-all"
              >
                <Play className="w-5 h-5" />
                Watch Demo
              </a>
            </div>
          </div>
        )}

        {/* Beta Benefits Section */}
        {activeSection === "benefits" && (
          <div className="space-y-10 animate-fade-in">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm mb-6">
                <Sparkles className="w-4 h-4" />
                Exclusive Beta Program
              </div>
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Beta Partner Benefits
              </h1>
              <p className="text-xl text-slate-400">What you gain by joining our Beta program</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {betaBenefits.map((benefit, idx) => (
                <div key={idx} className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${benefit.color} flex items-center justify-center mb-4`}>
                    <benefit.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-bold text-xl mb-3">{benefit.title}</h3>
                  <p className="text-slate-400">{benefit.description}</p>
                </div>
              ))}
            </div>

            {/* Additional Benefits */}
            <div className="bg-gradient-to-br from-violet-900/20 to-purple-900/20 border border-violet-500/20 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-violet-400">
                <Award className="w-6 h-6" />
                Beta Partner Exclusives
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { title: "Priority Feature Input", description: "Direct influence on the platform's feature roadmap" },
                  { title: "Discounted Pricing", description: "Significantly reduced rates during the Beta period" },
                  { title: "Priority Onboarding", description: "White-glove implementation and training" },
                  { title: "Direct Support Line", description: "24/7 access to our engineering team" },
                  { title: "QuickBid Priority", description: "First access to our contractor exchange network" },
                  { title: "Case Study Partnership", description: "Featured in our success story publications" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-4 bg-slate-800/50 rounded-xl">
                    <CheckCircle2 className="w-5 h-5 text-violet-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">{item.title}</p>
                      <p className="text-slate-400 text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Case Studies Section */}
        {activeSection === "casestudies" && (
          <div className="space-y-10 animate-fade-in">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Proven Results
              </h1>
              <p className="text-xl text-slate-400">Already making an impact in healthcare</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {caseStudies.map((study, idx) => (
                <div key={idx} className={`bg-gradient-to-br ${study.color.replace('from-', 'from-').replace('to-', 'to-')}/10 border border-slate-700/50 rounded-2xl p-6`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-xl">{study.name}</h3>
                    <span className={`px-3 py-1 bg-gradient-to-r ${study.color} text-white text-xs font-bold rounded-full`}>
                      {study.highlight}
                    </span>
                  </div>
                  <p className="text-slate-400 mb-4">{study.description}</p>
                  <div className="p-4 bg-slate-800/50 rounded-xl">
                    <p className="text-sm text-slate-500">Result</p>
                    <p className="font-medium">{study.result}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Traction */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Activity className="w-6 h-6 text-emerald-400" />
                Current Traction
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center p-6 bg-slate-900/50 rounded-xl">
                  <Hospital className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
                  <p className="text-3xl font-bold mb-1">1</p>
                  <p className="text-slate-400 text-sm">Live Beta Site</p>
                </div>
                <div className="text-center p-6 bg-slate-900/50 rounded-xl">
                  <Users className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
                  <p className="text-3xl font-bold mb-1">100+</p>
                  <p className="text-slate-400 text-sm">Pre-Vetted Contractors</p>
                </div>
                <div className="text-center p-6 bg-slate-900/50 rounded-xl">
                  <Globe className="w-10 h-10 text-violet-400 mx-auto mb-3" />
                  <p className="text-3xl font-bold mb-1">6</p>
                  <p className="text-slate-400 text-sm">Years Battle-Tested</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* FAQ Section */}
        {activeSection === "faq" && (
          <div className="space-y-10 animate-fade-in">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Frequently Asked Questions
              </h1>
              <p className="text-xl text-slate-400">Common questions about the Beta program</p>
            </div>

            <div className="space-y-4 max-w-3xl mx-auto">
              {hospitalFaqData.map((faq, idx) => (
                <div 
                  key={idx}
                  className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-700/30 transition-colors"
                  >
                    <span className="font-semibold pr-4">{faq.question}</span>
                    <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${expandedFaq === idx ? 'rotate-180' : ''}`} />
                  </button>
                  {expandedFaq === idx && (
                    <div className="px-5 pb-5 border-t border-slate-700/50">
                      <p className="text-slate-400 pt-4">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contact / Join Beta Section */}
        {activeSection === "contact" && (
          <div className="space-y-10 animate-fade-in">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm mb-6">
                <Rocket className="w-4 h-4" />
                Limited Beta Spots Available
              </div>
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Join the Beta Program
              </h1>
              <p className="text-xl text-slate-400">Be among the first to solve the $500K downtime crisis</p>
            </div>

            <div className="grid lg:grid-cols-2 gap-10">
              {/* Contact Form */}
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-8">
                <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-cyan-400" />
                  Request Beta Access
                </h2>
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Your Name</label>
                    <Input
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      placeholder="John Smith"
                      className="bg-slate-900/50 border-slate-700 text-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Email Address</label>
                    <Input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      placeholder="john@hospital.org"
                      className="bg-slate-900/50 border-slate-700 text-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Facility Name</label>
                    <Input
                      value={contactForm.facility}
                      onChange={(e) => setContactForm({ ...contactForm, facility: e.target.value })}
                      placeholder="Memorial Hospital"
                      className="bg-slate-900/50 border-slate-700 text-white"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Message (Optional)</label>
                    <Textarea
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      placeholder="Tell us about your facility and challenges..."
                      className="bg-slate-900/50 border-slate-700 text-white min-h-[100px]"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={formSubmitting}
                    className="w-full py-4 bg-gradient-to-r from-cyan-600 to-blue-500 hover:from-cyan-700 hover:to-blue-600"
                  >
                    {formSubmitting ? "Submitting..." : "Request Beta Access"}
                  </Button>
                </form>
              </div>

              {/* Contact Info */}
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-cyan-900/30 to-blue-900/30 border border-cyan-500/20 rounded-2xl p-8">
                  <img src={GEORGE_IMAGE} alt="George Broughton" className="w-20 h-20 rounded-full mx-auto mb-4 border-4 border-slate-700 object-cover" />
                  <h3 className="text-xl font-bold text-center mb-1">George Broughton III</h3>
                  <p className="text-cyan-400 text-center mb-4">CEO, Sterile Mate LLC / AskIsaac.io</p>
                  <div className="space-y-3">
                    <a href="tel:8009883892" className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg hover:bg-slate-700/50 transition-colors">
                      <Phone className="w-5 h-5 text-cyan-400" />
                      <span>800.988.3892</span>
                    </a>
                    <a href="mailto:george.broughton@sterilemate.com" className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg hover:bg-slate-700/50 transition-colors">
                      <Mail className="w-5 h-5 text-cyan-400" />
                      <span>george.broughton@sterilemate.com</span>
                    </a>
                    <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg">
                      <MapPin className="w-5 h-5 text-cyan-400" />
                      <span className="text-sm">396 W Greens Road, Suite 1139<br />Houston, TX 77067</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 text-center">
                  <h3 className="font-bold mb-3">Watch the Demo</h3>
                  <p className="text-slate-400 text-sm mb-4">See AskIsaac in action before scheduling a call</p>
                  <a 
                    href="https://sterilemate.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 rounded-xl transition-colors"
                  >
                    <Play className="w-5 h-5" />
                    Visit sterilemate.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-slate-700/50 bg-slate-900/80 mt-20">
        <div className="max-w-7xl mx-auto px-6 py-8 text-center">
          <p className="text-slate-500 text-sm">
            © 2026 AskIsaac Inc. All rights reserved. | <a href="https://sterilemate.com" className="text-cyan-400 hover:text-cyan-300">sterilemate.com</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
